/**
 * PATH: packages/ui/src/kanban/components/CardTeamsThreadMessage.tsx
 * FILE_ID: UI-KANBAN-TEAMS-MSG-001
 * PURPOSE: One message row in the card's Teams thread — header (sender,
 *          edited/pinned, timestamp), reply preview, body (with @mention
 *          highlighting), reactions, read receipts, and the OWN-message
 *          edit + delete affordances.
 * WHY:
 *   Operator: "when we mistype something in the threads and click to edit
 *   it, it opens the right-click (context menu) options." The thread had NO
 *   left-click edit path — the only way to reach editing was right-clicking
 *   a message, which just showed the browser's native context menu. This row
 *   now carries an explicit pencil button (left-click) that swaps the body
 *   for an inline editor wired to `editCardTeamMessage`
 *   (BUG-2026-07-KANBAN-THREAD-EDIT-CONTEXT-MENU).
 *
 *   Also extracted out of CardTeamsThreadSection.tsx so that frame stays a
 *   thin orchestrator under the 500-line ceiling (rule 13).
 * FLOW:
 *   ┌────────────┐   ┌───────────────┐   ┌────────────────────────┐
 *   │ pencil     │ → │ inline editor │ → │ onEdit(id, text) →     │
 *   │ (left-click)│   │ (textarea)    │   │ editCardTeamMessage    │
 *   └────────────┘   └───────────────┘   └────────────────────────┘
 * RELATED:
 *   - CardTeamsThreadSection.tsx: the thread frame that maps these rows.
 *   - api-fetch/trello-teams.ts editCardTeamMessage: the PATCH transport.
 *   - views_team_chat/message_views.py TeamMessageDetailAPI.patch: sender-
 *     only + 15-minute window the affordance mirrors.
 */
import { Check, Loader2, Pencil, Trash2, X } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'

import type { KanbanMember } from '../api'
import type { CardTeamMessage } from '../api'
import { formatDmyTime } from '../utils/localDate'
import { MessageBody } from './teams-thread'

/** Server enforces a 15-minute sender-only edit window; mirror it here so
 *  the pencil only shows while an edit would actually succeed (avoids a
 *  confusing 400 on a stale message). */
const EDIT_WINDOW_MS = 15 * 60 * 1000

function formatTime(iso: string): string {
  // Day-first `dd/mm/yyyy, HH:mm` so stamps match the rest of the drawer.
  return formatDmyTime(iso)
}

export interface CardTeamsThreadMessageProps {
  message: CardTeamMessage
  membersById: Map<number, KanbanMember>
  currentUserId?: number | null
  /** Delete is wired (api.deleteCardTeamMessage present). */
  canDelete: boolean
  deletePending: boolean
  onDelete: (messageId: string) => void
  /** Edit is wired (api.editCardTeamMessage present). */
  canEdit: boolean
  /** Resolves when the server has accepted the edit; rejects with the
   *  server detail so the row can surface it inline. */
  onEdit: (messageId: string, content: string) => Promise<void>
}

export function CardTeamsThreadMessage({
  message: m,
  membersById,
  currentUserId,
  canDelete,
  deletePending,
  onDelete,
  canEdit,
  onEdit,
}: CardTeamsThreadMessageProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(m.content)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const withinEditWindow =
    Date.now() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS
  const showEdit = canEdit && m.is_own && withinEditWindow

  const startEdit = () => {
    setDraft(m.content)
    setError(null)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setError(null)
  }

  const saveEdit = async () => {
    const next = draft.trim()
    if (!next || saving) return
    if (next === m.content.trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onEdit(m.id, next)
      setEditing(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save edit')
    } finally {
      setSaving(false)
    }
  }

  const onEditorKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  return (
    <li
      data-kanban-message-id={m.id}
      className={
        'group/msg rounded-md border px-2 py-1.5 text-xs ' +
        (m.is_own
          ? 'border-blue-100 bg-blue-50/40 dark:border-blue-900/30 dark:bg-blue-900/10'
          : 'border-neutral-100 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50')
      }
    >
      <div className="mb-0.5 flex items-center justify-between gap-2 text-[10px] text-neutral-500">
        <span className="font-medium text-neutral-700 dark:text-neutral-300">
          {m.sender.name || 'Unknown'}
          {m.is_edited ? ' · edited' : ''}
          {m.is_pinned ? ' · pinned' : ''}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <span className="font-mono">{formatTime(m.created_at)}</span>
          {showEdit && !editing && (
            <button
              type="button"
              onClick={startEdit}
              aria-label="Edit comment"
              title="Edit comment"
              className="rounded p-0.5 text-neutral-400 opacity-0 transition hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 group-hover/msg:opacity-100 dark:hover:bg-blue-950/30"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {canDelete && m.is_own && (
            <button
              type="button"
              onClick={() => {
                if (!deletePending && confirm('Delete this comment?')) {
                  onDelete(m.id)
                }
              }}
              disabled={deletePending}
              aria-label="Delete comment"
              title="Delete comment"
              className="rounded p-0.5 text-neutral-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover/msg:opacity-100 disabled:opacity-50 dark:hover:bg-red-950/30"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {m.reply_to && (
        <div className="mb-0.5 rounded border-l-2 border-neutral-300 bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900">
          <span className="font-medium">{m.reply_to.sender_name}</span>
          {': '}
          {m.reply_to.content}
        </div>
      )}

      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onEditorKeyDown}
            rows={2}
            autoFocus
            aria-label="Edit comment"
            className="w-full resize-none rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-neutral-800 dark:bg-neutral-900"
          />
          <div className="mt-1 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => void saveEdit()}
              disabled={!draft.trim() || saving}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-0.5 text-[11px] font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
            {error && <span className="text-[10px] text-red-500">{error}</span>}
          </div>
        </div>
      ) : (
        <div className="text-neutral-800 dark:text-neutral-100">
          <MessageBody
            content={m.content}
            mentions={m.mentions}
            membersById={membersById}
            currentUserId={currentUserId}
          />
        </div>
      )}

      {m.reactions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {m.reactions.map((r) => (
            <span
              key={r.emoji}
              title={r.users.join(', ')}
              className={
                'inline-flex items-center gap-0.5 rounded-full border px-1.5 text-[10px] ' +
                (r.includes_me
                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-900/20'
                  : 'border-neutral-200 bg-white text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900')
              }
            >
              <span>{r.emoji}</span>
              <span>{r.count}</span>
            </span>
          ))}
        </div>
      )}

      {m.read_by.length > 0 && (
        <div className="mt-0.5 text-[10px] text-neutral-400">
          Read by {m.read_by.slice(0, 3).join(', ')}
          {m.read_by.length > 3 && ` +${m.read_by.length - 3}`}
        </div>
      )}
    </li>
  )
}

export default CardTeamsThreadMessage
